import { useToast } from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  useBlackjackAreaGameStatus,
  useBlackjackPlayers,
} from '../../../../classes/BlackjackAreaController';
import { useBlackjackAreaController, useInteractable } from '../../../../classes/TownController';
import useTownController from '../../../../hooks/useTownController';
import BlackjackAreaInteractable from '../BlackjackArea';
import BlackjackGamePlayModal from './BlackjackGamePlayModal';
import JoinBlackjackGameModal from './JoinBlackjackGameModal';
import NewBlackjackGameModal from './NewBlackjackGameModal';
import StartBlackjackGameModal from './StartBlackjackGameModal';
import WaitBlackjackGameModal from './WaitBlackjackGameModal';

export function PreGameModal({
  blackjackArea,
  newIsOpen,
  setNewIsOpen,
  startIsOpen,
  setStartIsOpen,
  setJoinIsOpen,
  waitIsOpen,
  setWaitIsOpen,
  setGameIsOpen,
}: {
  blackjackArea: BlackjackAreaInteractable;
  newIsOpen: boolean;
  setNewIsOpen: (isOpen: boolean) => void;
  startIsOpen: boolean;
  setStartIsOpen: (isOpen: boolean) => void;
  joinIsOpen: boolean;
  setJoinIsOpen: (isOpen: boolean) => void;
  waitIsOpen: boolean;
  setWaitIsOpen: (isOpen: boolean) => void;
  gameIsOpen: boolean;
  setGameIsOpen: (isOpen: boolean) => void;
}): JSX.Element {
  const blackjackAreaController = useBlackjackAreaController(blackjackArea.name);
  const townController = useTownController();
  const players = useBlackjackPlayers(blackjackAreaController);
  const isGamePlaying = useBlackjackAreaGameStatus(blackjackAreaController);

  const thisPlayerId = townController.ourPlayer.id;

  if (newIsOpen) {
    return (
      <>
        <NewBlackjackGameModal
          isOpen={true}
          close={() => {
            setNewIsOpen(false);
            setStartIsOpen(true);
            setJoinIsOpen(true);
          }}
          newBlackjackGame={blackjackArea}
        />
      </>
    );
  } else if (startIsOpen) {
    if (thisPlayerId === players[0].id) {
      return (
        <>
          <StartBlackjackGameModal
            isOpen={true}
            onStartClick={() => {
              setStartIsOpen(false);
              setGameIsOpen(true);
              setJoinIsOpen(false);
              setWaitIsOpen(false);
            }}
            blackjackArea={blackjackArea}
          />
        </>
      );
    } else if (
      // joinIsOpen &&
      players.filter(eachPlayer => eachPlayer.id === thisPlayerId).length === 0
    ) {
      return (
        <>
          <JoinBlackjackGameModal
            isOpen={true}
            onJoin={() => {
              setJoinIsOpen(false);
              setWaitIsOpen(true);
            }}
            blackjackArea={blackjackArea}
          />
        </>
      );
    } else if (waitIsOpen) {
      return (
        <>
          <WaitBlackjackGameModal
            isOpen={waitIsOpen && !isGamePlaying}
            blackjackAreaController={blackjackAreaController}
          />
        </>
      );
    } else {
      return <></>;
    }
  } else {
    return <></>;
  }
}

export function BlackjackGameMap({
  blackjackArea,
}: {
  blackjackArea: BlackjackAreaInteractable;
}): JSX.Element {
  const blackjackAreaController = useBlackjackAreaController(blackjackArea.name);
  const townController = useTownController();
  const players = useBlackjackPlayers(blackjackAreaController);
  const isGamePlaying = useBlackjackAreaGameStatus(blackjackAreaController);
  const [newIsOpen, setNewIsOpen] = useState(players.length === 0);
  const [startIsOpen, setStartIsOpen] = useState(true);
  const [joinIsOpen, setJoinIsOpen] = useState(false);
  const [waitIsOpen, setWaitIsOpen] = useState(false);
  const [gameIsOpen, setGameIsOpen] = useState(false);

  const toast = useToast();
  if (isGamePlaying) {
    if (players.find(eachPlayer => eachPlayer.id === townController.ourPlayer.id) === undefined) {
      toast({ title: 'Already in game. Please wait for the next game.', status: 'error' });
      return <></>;
    } else {
      return (
        <>
          <BlackjackGamePlayModal isOpen={true} blackjackArea={blackjackArea} />
        </>
      );
    }
  } else {
    return (
      <PreGameModal
        blackjackArea={blackjackArea}
        newIsOpen={newIsOpen}
        setNewIsOpen={setNewIsOpen}
        joinIsOpen={joinIsOpen}
        setJoinIsOpen={setJoinIsOpen}
        waitIsOpen={waitIsOpen}
        setWaitIsOpen={setWaitIsOpen}
        startIsOpen={startIsOpen}
        setStartIsOpen={setStartIsOpen}
        gameIsOpen={gameIsOpen}
        setGameIsOpen={setGameIsOpen}
      />
    );
  }
}

/**
 * The ViewingAreaWrapper is suitable to be *always* rendered inside of a town, and
 * will activate only if the player begins interacting with a viewing area.
 */
export default function BlackjackGameMapWrapper(): JSX.Element {
  const blackjackArea = useInteractable<BlackjackAreaInteractable>('blackjackArea');
  if (blackjackArea !== undefined) {
    return <BlackjackGameMap blackjackArea={blackjackArea} />;
  }
  return <></>;
}
